/**
 * API client pentru Staff (Angajați)
 */

import apiClient from '../services/api'

// ============ TYPES ============

export interface Staff {
  id: string
  tenant_id: string
  first_name: string
  last_name: string
  staff_type: StaffType
  cnp?: string
  phone?: string
  email?: string
  cod_parafa?: string
  modalities?: string[]
  is_coordinator: boolean
  teleradiology_only: boolean
  active: boolean
  notes?: string
  user_id?: string
  user_email?: string
  has_user_account: boolean
  created_at: string
  updated_at: string
  full_name?: string
  display_name?: string
}

export type StaffType = 'doctor' | 'technician' | 'nurse' | 'receptionist' | 'operator' | 'admin'

export interface InstitutionStaffAssignment {
  id: string
  institution_id: string
  staff_id: string
  role_in_institution: string
  is_primary: boolean
  can_be_assigned_studies: boolean
  active: boolean
  created_at: string
  institution_name?: string
  institution_code?: string
}

export interface StaffWithInstitutions extends Staff {
  institution_assignments: InstitutionStaffAssignment[]
}

export interface StaffListResponse {
  items: Staff[]
  total: number
  page: number
  page_size: number
  pages: number
}

export interface StaffSelectOption {
  id: string
  full_name: string
  display_name: string
  staff_type: StaffType
  institution_ids: string[]
}

export interface StaffCreateData {
  first_name: string
  last_name: string
  staff_type: StaffType
  cnp?: string
  phone?: string
  email?: string
  cod_parafa?: string
  modalities?: string[]
  is_coordinator?: boolean
  teleradiology_only?: boolean
  active?: boolean
  notes?: string
  institution_ids?: string[]
  // Creare cont user
  create_user_account?: boolean
  user_email?: string
  user_password?: string
  user_role_id?: string
}

export interface StaffUpdateData {
  first_name?: string
  last_name?: string
  staff_type?: StaffType
  cnp?: string
  phone?: string
  email?: string
  cod_parafa?: string
  modalities?: string[]
  is_coordinator?: boolean
  teleradiology_only?: boolean
  active?: boolean
  notes?: string
  user_id?: string
}

export interface StaffFilter {
  search?: string
  staff_type?: StaffType
  institution_id?: string
  active?: boolean
  has_user_account?: boolean
  is_coordinator?: boolean
  teleradiology_only?: boolean
  page?: number
  page_size?: number
}

export interface InstitutionStaffCreateData {
  institution_id: string
  staff_id: string
  role_in_institution: string
  is_primary?: boolean
  can_be_assigned_studies?: boolean
}

export interface InstitutionStaffUpdateData {
  role_in_institution?: string
  is_primary?: boolean
  can_be_assigned_studies?: boolean
  active?: boolean
}

export interface StaffMigrationResult {
  total_users_processed: number
  staff_created: number
  staff_skipped: number
  institution_assignments_created: number
  errors: string[]
}

// ============ API FUNCTIONS ============

/**
 * Listează angajații cu filtre și paginare
 */
export const getStaffList = async (filters: StaffFilter = {}): Promise<StaffListResponse> => {
  const params = new URLSearchParams()
  
  if (filters.search) params.append('search', filters.search)
  if (filters.staff_type) params.append('staff_type', filters.staff_type)
  if (filters.institution_id) params.append('institution_id', filters.institution_id)
  if (filters.active !== undefined) params.append('active', String(filters.active))
  if (filters.has_user_account !== undefined) params.append('has_user_account', String(filters.has_user_account))
  if (filters.is_coordinator !== undefined) params.append('is_coordinator', String(filters.is_coordinator))
  if (filters.teleradiology_only !== undefined) params.append('teleradiology_only', String(filters.teleradiology_only))
  if (filters.page) params.append('page', String(filters.page))
  if (filters.page_size) params.append('page_size', String(filters.page_size))
  
  const response = await apiClient.get<StaffListResponse>(`/staff?${params.toString()}`)
  return response.data
}

/**
 * Obține opțiuni pentru dropdown/select
 */
export const getStaffSelectOptions = async (
  staffType?: StaffType,
  institutionId?: string,
  activeOnly: boolean = true
): Promise<StaffSelectOption[]> => {
  const params = new URLSearchParams()
  
  if (staffType) params.append('staff_type', staffType)
  if (institutionId) params.append('institution_id', institutionId)
  params.append('active_only', String(activeOnly))
  
  const response = await apiClient.get<StaffSelectOption[]>(`/staff/select-options?${params.toString()}`)
  return response.data
}

/**
 * Obține detaliile unui angajat
 */
export const getStaff = async (staffId: string): Promise<StaffWithInstitutions> => {
  const response = await apiClient.get<StaffWithInstitutions>(`/staff/${staffId}`)
  return response.data
}

/**
 * Creează un nou angajat
 */
export const createStaff = async (data: StaffCreateData): Promise<StaffWithInstitutions> => {
  const response = await apiClient.post<StaffWithInstitutions>('/staff', data)
  return response.data
}

/**
 * Actualizează un angajat
 */
export const updateStaff = async (staffId: string, data: StaffUpdateData): Promise<StaffWithInstitutions> => {
  const response = await apiClient.put<StaffWithInstitutions>(`/staff/${staffId}`, data)
  return response.data
}

/**
 * Șterge (soft delete) un angajat
 */
export const deleteStaff = async (staffId: string): Promise<void> => {
  await apiClient.delete(`/staff/${staffId}`)
}

/**
 * Adaugă un angajat într-o instituție
 */
export const addStaffToInstitution = async (
  staffId: string,
  data: Omit<InstitutionStaffCreateData, 'staff_id'>
): Promise<InstitutionStaffAssignment> => {
  const response = await apiClient.post<InstitutionStaffAssignment>(
    `/staff/${staffId}/institutions`,
    { ...data, staff_id: staffId }
  )
  return response.data
}

/**
 * Actualizează rolul unui angajat într-o instituție
 */
export const updateStaffInstitution = async (
  staffId: string,
  institutionId: string,
  data: InstitutionStaffUpdateData
): Promise<InstitutionStaffAssignment> => {
  const response = await apiClient.put<InstitutionStaffAssignment>(
    `/staff/${staffId}/institutions/${institutionId}`,
    data
  )
  return response.data
}

/**
 * Elimină un angajat dintr-o instituție
 */
export const removeStaffFromInstitution = async (
  staffId: string,
  institutionId: string
): Promise<void> => {
  await apiClient.delete(`/staff/${staffId}/institutions/${institutionId}`)
}

/**
 * Migrează utilizatorii existenți în Staff
 */
export const migrateUsersToStaff = async (dryRun: boolean = true): Promise<StaffMigrationResult> => {
  const response = await apiClient.post<StaffMigrationResult>(
    `/staff/migrate-from-users?dry_run=${dryRun}`
  )
  return response.data
}

// ============ UTILITY FUNCTIONS ============

/**
 * Returnează label-ul pentru un tip de angajat
 */
export const getStaffTypeLabel = (type: StaffType): string => {
  const labels: Record<StaffType, string> = {
    doctor: 'Medic',
    technician: 'Tehnician',
    nurse: 'Asistentă',
    receptionist: 'Recepționist',
    operator: 'Operator',
    admin: 'Administrator',
  }
  return labels[type] || type
}

/**
 * Returnează toate tipurile de angajați
 */
export const getAllStaffTypes = (): { value: StaffType; label: string }[] => [
  { value: 'doctor', label: 'Medic' },
  { value: 'technician', label: 'Tehnician' },
  { value: 'nurse', label: 'Asistentă' },
  { value: 'receptionist', label: 'Recepționist' },
  { value: 'operator', label: 'Operator' },
  { value: 'admin', label: 'Administrator' },
]
